import React, { useMemo } from 'react';
import {
  Page,
  Document,
  StyleSheet,
  View,
  Font,
  Text,
} from '@react-pdf/renderer';
import { Pickup } from 'types/pickup';
import { convertUTCToLocal } from 'utils/dateUtils';
import { SKU } from 'types/sku';
import StarBadge from './SVG/StarBadge';
import Truck from './SVG/Truck';

Font.register({
  family: 'Trebuchet',
  fonts: [
    {
      src: require('assets/impact-report/trebucbd.ttf'),
      fontWeight: 'bold',
    },
    {
      src: require('assets/impact-report/trebuc.ttf'),
      fontWeight: 'normal',
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    paddingTop: 40,
    paddingHorizontal: 40,
    fontFamily: 'Trebuchet',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
    color: '#000',
  },
  divider: {
    height: 1,
    backgroundColor: '#000',
  },
  section: {
    border: '1px solid #EBEFF6',
    padding: 10,
    borderRadius: 5,
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  infoText: {
    flexDirection: 'column',
    gap: 2,
  },
  label: {
    fontSize: 8,
    color: '#888',
    textTransform: 'uppercase',
  },
  value: {
    fontSize: 10,
    color: '#000',
    fontWeight: 'bold',
  },
  fieldLabel: {
    fontSize: 10,
    color: '#000',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  fieldLabelLight: {
    fontSize: 8,
    color: '#888',
    fontWeight: 'normal',
    textTransform: 'uppercase',
  },
  line: {
    flex: 1,
    height: 1,
    backgroundColor: '#000',
  },
  table: {
    borderWidth: 1,
    borderColor: '#4CAF50',
    marginTop: 20,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#4CAF50',
    minHeight: 25,
  },
  tableHeaderRow: {
    backgroundColor: '#f6fffa',
  },
  tableFooterRow: {
    backgroundColor: '#d2f8d2',
  },
  skuColumn: {
    width: '20%',
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: '#4CAF50',
    justifyContent: 'center',
  },
  tableColumn: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 50,
  },
  lastColumn: {
    borderRightWidth: 0,
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableCellText: {
    fontSize: 8,
    textAlign: 'center',
  },
  skuText: {
    fontSize: 8,
    textAlign: 'left',
  },
  commentsBox: {
    width: '100%',
    height: 60,
    border: '1px solid #ccc',
    borderRadius: 4,
    marginTop: 10,
  },
});

type PickupDetailsPdfTemplateProps = {
  data: Pickup | null;
  skuData: SKU[];
  isRacked?: boolean;
};

// Reusable Components
const InfoItem = ({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) => (
  <View style={styles.infoItem}>
    {icon}
    <View style={styles.infoText}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{value}</Text>
    </View>
  </View>
);

const FormField = ({
  label,
  flex = 1,
  hideLine = false,
}: {
  label: string;
  flex?: number;
  hideLine?: boolean;
}) => (
  <View style={[styles.row, { flex, alignItems: 'flex-end' }]}>
    <Text style={styles.fieldLabel}>{label}</Text>
    {!hideLine && <View style={styles.line} />}
  </View>
);

const FormFieldWithSuffix = ({
  label,
  suffix,
  flex = 1,
}: {
  label: string;
  suffix: string;
  flex?: number;
}) => (
  <View style={[styles.row, { flex, alignItems: 'flex-end' }]}>
    <View style={styles.line} />
    <Text style={styles.fieldLabelLight}>{suffix}</Text>
  </View>
);

const TableCell = ({
  children,
  isHeader = false,
  isSku = false,
  isLast = false,
  style,
}: {
  children?: React.ReactNode;
  isHeader?: boolean;
  isSku?: boolean;
  isLast?: boolean;
  style?: any;
}) => (
  <View
    style={[
      isSku ? styles.skuColumn : styles.tableColumn,
      ...(isLast ? [styles.lastColumn] : []),
      style,
    ]}>
    <Text
      style={[
        isHeader
          ? styles.tableHeaderText
          : isSku
          ? styles.skuText
          : styles.tableCellText,
        ...(!isHeader && isSku ? [{ fontWeight: 'bold' as const }] : []),
      ]}>
      {children}
    </Text>
  </View>
);

const TableRow = ({
  children,
  isHeader = false,
  isFooter = false,
}: {
  children: React.ReactNode;
  isHeader?: boolean;
  isFooter?: boolean;
}) => (
  <View
    style={[
      styles.tableRow,
      ...(isHeader ? [styles.tableHeaderRow] : []),
      ...(isFooter ? [styles.tableFooterRow] : []),
    ]}>
    {children}
  </View>
);

const PickupDetailsPDFTemplate = ({
  data = null,
  skuData,
  isRacked = false,
}: PickupDetailsPdfTemplateProps) => {
  return (
    <Document>
      <PickupDetailsPage data={data!} isRacked={isRacked} skuData={skuData} />
    </Document>
  );
};

export const PickupDetailsPage = ({
  data,
  isRacked = false,
  skuData,
}: {
  data: Pickup;
  isRacked?: boolean;
  skuData: SKU[];
}) => {
  const pickupDateValue = convertUTCToLocal(data?.date, true, true);
  const pickupDate =
    typeof pickupDateValue === 'string' ? pickupDateValue : 'May 2, 2025';

  const tableData = useMemo(() => {
    return skuData.map((sku) => ({
      sku: sku.name,
      singles: '',
      damaged: '',
      racks: '',
      itemsPerRack: '',
      stacks: '',
      itemsPerStack: '',
      total: '',
    }));
  }, [skuData]);

  return (
    <Page size="A4" style={styles.page}>
      <View style={{ gap: 10 }}>
        <Text style={styles.title}>Pickup Harvest</Text>
        <View style={styles.divider} />

        <View
          style={[
            styles.section,
            { flexDirection: 'row', justifyContent: 'space-between' },
          ]}>
          <InfoItem
            icon={<StarBadge />}
            label="SITE"
            value={data?.site?.name ?? data?.collectionPoint?.site?.name ?? ''}
          />
          <InfoItem icon={<Truck />} label="PICKUP DATE:" value={pickupDate} />
          <InfoItem
            label="PICKUP"
            value={data?.pickupNumber ? `#${data.pickupNumber}` : '#2213'}
          />
        </View>

        {/* Collection Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>1. Collection</Text>
          <View style={[styles.row, { width: '100%', gap: 10 }]}>
            <View style={[styles.row, { flex: 1 }]}>
              <FormField label="DRIVER" />
              <FormField label="# OF BINS COLLECTED" hideLine />
            </View>
            <View style={[styles.row, { flex: 1 }]}>
              <FormFieldWithSuffix label="" suffix="96 Gal" />
              <FormFieldWithSuffix label="" suffix="Dumpster" />
            </View>
          </View>
        </View>

        {/* Sort KPI Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2. Sort KPI</Text>
          <View
            style={[styles.row, { width: '100%', gap: 10, marginBottom: 10 }]}>
            <View style={[styles.row, { flex: 1 }]}>
              <FormField label="Sort Date:" />
              <FormField label="# Of Employees:" />
            </View>
            <View style={[styles.row, { flex: 1 }]}>
              <FormField label="Total Time:" hideLine />
              <FormFieldWithSuffix label="" suffix="Hours" />
              <FormFieldWithSuffix label="" suffix="Minutes" />
            </View>
          </View>
          <View style={[styles.row, { width: '100%', gap: 10 }]}>
            <View style={[styles.row, { flex: 1 }]}>
              <FormField label="Start Time:" />
              <FormField label="End Time:" />
            </View>
            <View style={[styles.row, { flex: 1 }]}>
              <FormField label="Garbage Collected:" hideLine />
              <FormFieldWithSuffix label="" suffix="Bags or Lbs" />
            </View>
          </View>
        </View>
      </View>

      {/* Table */}
      <View style={styles.table}>
        <TableRow isHeader>
          <TableCell isHeader isSku>
            SKU
          </TableCell>

          {isRacked && (
            <>
              <TableCell
                style={{
                  borderTop: '1px solid #4CAF50',
                  borderLeft: '1px solid #4CAF50',
                }}
                isHeader>
                Racks
              </TableCell>
              <TableCell
                style={{
                  borderTop: '1px solid #4CAF50',
                }}
                isHeader>
                Items Per Rack
              </TableCell>
              <TableCell
                style={{
                  borderTop: '1px solid #4CAF50',
                  borderRight: '1px solid #4CAF50',
                }}
                isHeader>
                Total Racked
              </TableCell>
            </>
          )}

          {!isRacked && (
            <>
              <TableCell
                style={{
                  borderTop: '1px solid #4CAF50',
                  borderLeft: '1px solid #4CAF50',
                }}
                isHeader>
                Stacks
              </TableCell>
              <TableCell
                style={{
                  borderTop: '1px solid #4CAF50',
                }}
                isHeader>
                Items Per Stack
              </TableCell>
              <TableCell
                style={{
                  borderTop: '1px solid #4CAF50',
                  borderRight: '1px solid #4CAF50',
                }}
                isHeader>
                Total Stacked
              </TableCell>
            </>
          )}

          <TableCell
            isHeader
            style={{
              borderTop: '1px solid #4CAF50',
              borderLeft: '1px solid #4CAF50',
            }}>
            Singles
          </TableCell>
          <TableCell
            isHeader
            style={{
              borderTop: '1px solid #4CAF50',
              borderRight: '1px solid #4CAF50',
            }}>
            Damaged
          </TableCell>
          <TableCell
            isHeader
            isLast
            style={{
              borderTop: '1px solid #4CAF50',
              borderRight: '1px solid #4CAF50',
            }}>
            Total
          </TableCell>
        </TableRow>

        {[...Array(10)].map((_, index) => {
          const row = tableData[index] || {
            sku: '',
            racks: '',
            itemsPerRack: '',
            stacks: '',
            itemsPerStack: '',
            singles: '',
            damaged: '',
            total: '',
          };

          return (
            <TableRow key={index}>
              <TableCell isSku>{row.sku}</TableCell>

              {isRacked && (
                <>
                  <TableCell style={{ borderLeft: '1px solid #4CAF50' }}>
                    {row.racks || ''}
                  </TableCell>
                  <TableCell>{row.itemsPerRack || ''}</TableCell>
                  <TableCell style={{ borderRight: '1px solid #4CAF50' }}>
                    {row.total || ''}
                  </TableCell>
                </>
              )}

              {!isRacked && (
                <>
                  <TableCell style={{ borderLeft: '1px solid #4CAF50' }}>
                    {row.stacks || ''}
                  </TableCell>
                  <TableCell>{row.itemsPerStack || ''}</TableCell>
                  <TableCell style={{ borderRight: '1px solid #4CAF50' }}>
                    {row.total || ''}
                  </TableCell>
                </>
              )}

              <TableCell style={{ borderLeft: '1px solid #4CAF50' }}>
                {row.singles || ''}
              </TableCell>
              <TableCell style={{ borderRight: '1px solid #4CAF50' }}>
                {row.damaged || ''}
              </TableCell>
              <TableCell isLast style={{ borderRight: '1px solid #4CAF50' }}>
                {row.total || ''}
              </TableCell>
            </TableRow>
          );
        })}

        <TableRow isFooter>
          <TableCell isSku>Total</TableCell>

          <TableCell
            style={{
              borderLeft: '1px solid #4CAF50',
              borderBottom: '1px solid #4CAF50',
            }}></TableCell>
          <TableCell style={{ borderBottom: '1px solid #4CAF50' }}></TableCell>
          <TableCell
            style={{
              borderRight: '1px solid #4CAF50',
              borderBottom: '1px solid #4CAF50',
            }}></TableCell>

          <TableCell
            style={{
              borderLeft: '1px solid #4CAF50',
              borderBottom: '1px solid #4CAF50',
            }}></TableCell>
          <TableCell
            style={{
              borderRight: '1px solid #4CAF50',
              borderBottom: '1px solid #4CAF50',
            }}></TableCell>
          <TableCell
            isLast
            style={{
              borderRight: '1px solid #4CAF50',
              borderBottom: '1px solid #4CAF50',
            }}></TableCell>
        </TableRow>
      </View>

      <View style={[styles.divider, { marginTop: 20 }]} />

      {/* Bottom Section */}
      <View style={{ gap: 20, marginTop: 20 }}>
        <View style={[styles.row, { width: '100%', gap: 10 }]}>
          <FormField label="Counted By:" />
          <FormField label="Date into rTurn:" />
        </View>
        <View>
          <Text style={styles.fieldLabel}>Breakdown Comments</Text>
          <View style={styles.commentsBox} />
        </View>
      </View>
    </Page>
  );
};

export default PickupDetailsPDFTemplate;
